import { Injectable } from '@angular/core';

import { Logger } from '@frontend/vanilla/core';
import Dexie from 'dexie';

import { Subcategory } from '../casino-lobby-manager/lobby-details-model/categories.model';
import { ConfigProviderService } from '../config-provider/config-provider.service';
import {
    GameMetadataTable,
    INDEXDB_NAME,
    INDEXDB_SCHEMA,
    LobbyTable,
    StickerDetailsTable,
    SubCategoryTable,
    TABLES,
    categoryNameLangTable,
    categoryVsFeatureMetadataTable,
} from './casino-index-db.model';
import { CommonIndexDBService } from './common-index-db.service';

@Injectable({
    providedIn: 'root',
})
export class CasinoIndexDBService {
    private db: Dexie | undefined;
    private addedLobbiesMap = new Set<string>();

    constructor(
        private commonIndexDbService: CommonIndexDBService,
        private logger: Logger,
        private configProviderService: ConfigProviderService,
    ) {
        this.setUpCasinoIndexDb();
    }

    setUpCasinoIndexDb() {
        this.db = this.commonIndexDbService.createConnection(INDEXDB_NAME, INDEXDB_SCHEMA);
    }

    private ensureDbInitialized(): void {
        if (!this.db) {
            throw new Error('Database is not initialized');
        }
    }

    async getLobby(lobbyType: string, dataIdentifierKey: string) {
        if (!dataIdentifierKey || !lobbyType) {
            this.logger.error(`Invalid parameters provided! dataIdentifierKey: ${dataIdentifierKey}, lobbyType: ${lobbyType}`);
            return null;
        }

        try {
            this.ensureDbInitialized();

            let lobby: LobbyTable | undefined = await this.db
                ?.table(TABLES.LOBBY)
                .where('[dataIdentifierKey+lobbyType]')
                .equals([dataIdentifierKey, lobbyType])
                .first();
            if (!lobby) {
                this.logger.info('Lobby not found!');
                return null;
            }
            return lobby;
        } catch (error) {
            this.logger.error('Failed to get lobby:', error);
            return null;
        }
    }

    async getLobbyPerCollectionKey(): Promise<LobbyTable[]> {
        try {
            this.ensureDbInitialized();
            let allLobbies = await this.db?.table(TABLES.LOBBY).toArray();
            const lobbyPerCollectionKey: LobbyTable[] = Object.values(
                allLobbies?.reduce((lobbiesWithKey, lobby) => {
                    if (!lobbiesWithKey[lobby.collectionKey]) {
                        lobbiesWithKey[lobby.collectionKey] = lobby;
                    }
                    return lobbiesWithKey;
                }, {}),
            );
            return lobbyPerCollectionKey.length ? lobbyPerCollectionKey : [];
        } catch (error) {
            this.logger.error('Failed to get lobby:', error);
            return [];
        }
    }

    async clearExpiredDataSet(): Promise<void> {
        try {
            const expirationInterval: any = this.configProviderService.provideFeaturesConfig().browserLocalStorageCategoryDataUpdateInterval * 1000;
            const dataSets: LobbyTable[] = await this.getLobbyPerCollectionKey();

            for (const lobby of dataSets) {
                let isLobbyDataExpired = Date.now() - lobby.createdAt >= expirationInterval;
                if (isLobbyDataExpired) {
                    await this.clearAllTablesForCollectionKey(lobby.collectionKey ? lobby.collectionKey : '');
                    this.logger.info('lobby details Expired for', lobby.collectionKey);
                }
            }
        } catch (error) {
            this.logger.error('Failed to get lobby:', error);
        }
    }

    async getCategories(lobbyType: string, dataIdentifierKey: string) {
        if (!lobbyType || !dataIdentifierKey) {
            this.logger.info('Invalid lobbyType provided!');
            return null;
        }
        try {
            this.ensureDbInitialized();
            const categories = await this.db
                ?.table(TABLES.CATEGORY)
                .where('[dataIdentifierKey+lobbyType]')
                .equals([dataIdentifierKey, lobbyType])
                .toArray();
            if (!categories || categories.length === 0) {
                this.logger.info('No categories found for the given dataIdentifierKey!');
                return null;
            }
            return categories;
        } catch (error) {
            this.logger.error('Failed to get categories:', error);
            return null;
        }
    }

    async getTransformedSubCategories(lobbyType: string, dataIdentifierKey: string): Promise<Subcategory[]> {
        try {
            this.ensureDbInitialized();
            const subcategories = await this.db
                ?.table(TABLES.SUB_CATEGORY)
                .where('[dataIdentifierKey+lobbyType]')
                .equals([dataIdentifierKey, lobbyType])
                .toArray();
            if (!subcategories || subcategories.length === 0) {
                this.logger.info('No subcategories found!');
                return [];
            }
            return subcategories.map((subcategory: SubCategoryTable) => ({
                subcategoryid: subcategory.subcategoryId,
                route: subcategory.route,
                subcategoryname: subcategory.subCategoryName,
                lmtCategoryId: subcategory.lmtCategoryId,
                categoryid: subcategory.categoryId,
                engCategoryName: subcategory.engCategoryName,
                categoryname: subcategory.categoryName,
                sitecoreData: subcategory.sitecoreData,
                parentCategoryId: subcategory.parentCategoryId,
                parentLMTCategoryId: subcategory.parentLMTCategoryId,
                linkedCategory: subcategory.linkedCategory,
                rowCount: subcategory.rowCount,
                subTitle: subcategory.subTitle,
                isSubCatVisible: subcategory.isSubCatVisible,
                filterRecentlyPlayedGames: subcategory.filterRecentlyPlayedGames,
                havingCasiaGames: subcategory.havingCasiaGames,
                gamelist: subcategory.games,
                hideSubCategory: subcategory.hideSubCategory,
                filterFeatureIds: subcategory.filterFeatureIds,
                lmtSubcategories: [],
                subcategories: [],
                isNavigationCategory: false,
            }));
        } catch (error) {
            this.logger.error('Failed to get subcategories:', error);
            return [];
        }
    }

    async getStickerDetails(lobbyType: string, dataIdentifierKey: string): Promise<StickerDetailsTable[] | undefined> {
        try {
            this.ensureDbInitialized();
            const stickerDetails = await this.db
                ?.table(TABLES.STICKER_DETAILS)
                .where('[dataIdentifierKey+lobbyType]')
                .equals([dataIdentifierKey, lobbyType])
                .toArray();
            return stickerDetails;
        } catch (error) {
            this.logger.error('Failed to get sticker details:', error);
            return [];
        }
    }

    async getGameMetadata(lobbyType: string, dataIdentifierKey: string): Promise<GameMetadataTable[] | undefined> {
        try {
            this.ensureDbInitialized();
            return this.db?.table(TABLES.GAME_METADATA).where('[dataIdentifierKey+lobbyType]').equals([dataIdentifierKey, lobbyType]).toArray();
        } catch (error) {
            this.logger.error('Failed to get gamemetadata:', error);
            return [];
        }
    }

    async getCatVsFeatureMetadata(lobbyType: string, dataIdentifierKey: string): Promise<categoryVsFeatureMetadataTable[] | []> {
        try {
            this.ensureDbInitialized();
            if (this.db) {
                const data = await this.db
                    ?.table(TABLES.CATEGORY_VS_FEATURE_METADATA)
                    .where('[dataIdentifierKey+lobbyType]')
                    .equals([dataIdentifierKey, lobbyType])
                    .toArray();
                return data;
            }
            return [];
        } catch (error) {
            this.logger.error('Failed to get CategoryVsfeature MetaData:', error);
            return [];
        }
    }

    async getCategoryNameLangMap(lobbyType: string, dataIdentifierKey: string): Promise<categoryNameLangTable[] | undefined> {
        try {
            this.ensureDbInitialized();
            const categoryNameLangMap = await this.db
                ?.table(TABLES.CATEGORY_NAME_LANG_MAP)
                .where('[dataIdentifierKey+lobbyType]')
                .equals([dataIdentifierKey, lobbyType])
                .toArray();
            return categoryNameLangMap;
        } catch (error) {
            this.logger.error('Failed to get sticker details:', error);
            return [];
        }
    }

    async addRecords<T>(tableName: string, records: T[]) {
        try {
            this.ensureDbInitialized();

            const table = this.db?.table(tableName);
            if (!records || records.length < 1) {
                return;
            }
            await table?.bulkAdd(records);
        } catch (error) {
            this.logger.error(`Failed to add records to ${tableName}:`, error);
        }
    }

    async addRecord<T>(tableName: string, record: T) {
        try {
            this.ensureDbInitialized();

            const table = this.db?.table(tableName);
            if (!record) {
                return;
            }
            await table?.add(record);
        } catch (error) {
            this.logger.error(`Failed to add records to ${tableName}:`, error);
        }
    }

    async checkCasinoIndexDBExpired(lobbyType: string, dataIdentifierKey: string, expirationInterval: number): Promise<boolean> {
        const lobbyData: LobbyTable | null = await this.getLobby(lobbyType, dataIdentifierKey);
        if (!lobbyData || !lobbyData.createdAt) {
            return true;
        }

        return Date.now() - lobbyData.createdAt >= expirationInterval;
    }

    async clearAllTablesForCollectionKey(collectionKey: string) {
        await this.commonIndexDbService.clearAllTablesRecordsForCollectionKey(INDEXDB_NAME, collectionKey);
        this.clearAddedLobbies(collectionKey);
        this.logger.info('Cleared tables');
    }

    hasLobbyBeenAdded(uniqueKey: string): boolean {
        return this.addedLobbiesMap.has(uniqueKey);
    }

    markLobbyAsAdded(uniqueKey: string): void {
        this.addedLobbiesMap.add(uniqueKey);
    }

    clearAddedLobbies(collectionKey: string): void {
        for (const key of Array.from(this.addedLobbiesMap)) {
            if (key.startsWith(collectionKey)) {
                this.addedLobbiesMap.delete(key);
            }
        }
    }
}
